import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { claimPendingSubscription, verifyResumeToken } from '../../services/stripeService';
import LoadingSpinner from '../LoadingSpinner';
import './AuthPages.css';

const STRIPE_CHECKOUT_SESSION_KEY = 'gallop_stripe_checkout_session';
const SESSION_RESUME_OK = 'gallop_register_resume_ok';
const SESSION_RESUME_EMAIL = 'gallop_resume_prefill_email';

const RegisterPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [checkoutGatePassed, setCheckoutGatePassed] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [fromStripeCheckout, setFromStripeCheckout] = useState(false);
  const { signUp } = useAuth();

  useEffect(() => {
    let cancelled = false;

    const resumeToken = searchParams.get('resume_token');
    if (resumeToken) {
      (async () => {
        try {
          const r = await verifyResumeToken(resumeToken);
          if (cancelled) return;
          if (r.ok) {
            sessionStorage.setItem(SESSION_RESUME_OK, '1');
            sessionStorage.setItem(SESSION_RESUME_EMAIL, r.email);
            setEmail(r.email);
            setFromStripeCheckout(true);
            setCheckoutGatePassed(true);
            navigate('/register', { replace: true });
          } else {
            navigate('/resume-signup', { replace: true, state: { error: r.error } });
          }
        } catch (e) {
          if (!cancelled) {
            navigate('/resume-signup', {
              replace: true,
              state: { error: e instanceof Error ? e.message : 'Verification failed' },
            });
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    if (sessionStorage.getItem(SESSION_RESUME_OK) === '1') {
      const pre = sessionStorage.getItem(SESSION_RESUME_EMAIL);
      if (pre) setEmail(pre);
      setFromStripeCheckout(true);
      setCheckoutGatePassed(true);
      return;
    }

    const sid = searchParams.get('stripe_session');
    if (sid) {
      sessionStorage.setItem(STRIPE_CHECKOUT_SESSION_KEY, sid);
      setFromStripeCheckout(true);
      setCheckoutGatePassed(true);
      return;
    }
    if (sessionStorage.getItem(STRIPE_CHECKOUT_SESSION_KEY)) {
      setFromStripeCheckout(true);
      setCheckoutGatePassed(true);
      return;
    }
    navigate('/resume-signup', { replace: true });
  }, [searchParams, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    const { error, session } = await signUp(email, password);

    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }

    const checkoutId = sessionStorage.getItem(STRIPE_CHECKOUT_SESSION_KEY);

    if (session?.access_token) {
      try {
        await claimPendingSubscription(session.access_token, checkoutId || undefined);
        sessionStorage.removeItem(STRIPE_CHECKOUT_SESSION_KEY);
      } catch (claimErr) {
        setError(
          claimErr instanceof Error
            ? claimErr.message
            : 'Could not link your subscription. Use the same email you used at Stripe, or sign in after confirming your email.'
        );
        setLoading(false);
        return;
      }
    }

    sessionStorage.removeItem(SESSION_RESUME_OK);
    sessionStorage.removeItem(SESSION_RESUME_EMAIL);

    setSuccess(true);
    setLoading(false);
  };

  if (!checkoutGatePassed) {
    return (
      <div className="auth-container">
        <div className="auth-card" style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Check Your Email</h1>
            <p>We've sent you a confirmation link to verify your account.</p>
          </div>
          <div className="success-message">
            Please check your email and click the confirmation link to complete your registration.
            {fromStripeCheckout && (
              <span>
                {' '}
                After you confirm, sign in with the same email you used at Stripe so we can finish linking your trial.
              </span>
            )}
          </div>
          <div className="auth-footer">
            <Link to="/login" className="auth-link">
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/dark_favicon_logo.png" alt="Gallop AI" className="auth-logo" />
          <h1>Create your GallopAI account</h1>
          <p>Use the same email you used when you subscribed.</p>
        </div>

        <div className="success-message" style={{ marginBottom: '1rem', textAlign: 'left' }}>
          This page is only available after checkout. Use the <strong>same email address</strong> as Stripe so your
          subscription links to this login.
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Create a password (min 6 characters)"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="auth-button primary"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <Link to="/login" className="auth-link">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
